import { Fragment } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconHeartFilled } from '@tabler/icons-react';
import { buildPropertyPhotoUrl } from 'components/marketplace/property/helpers';
import { PropertyPhotoImage } from 'components/marketplace/shared/PropertyPhoto';
import { Popup } from 'react-map-gl/mapbox';

import type { MapMarkerProperty } from './types';

import styles from './MapMarker.module.css';

function buildPhotoSlots(photos: readonly string[] | null | undefined) {
  const [primary, secondary, tertiary] = photos ?? [];
  return {
    primary: buildPropertyPhotoUrl(primary, { preset: '3:2-sm' }),
    secondary: buildPropertyPhotoUrl(secondary, { preset: '3:2-xs' }),
    tertiary: buildPropertyPhotoUrl(tertiary, { preset: '3:2-xs' }),
  };
}

export function buildMarkerAriaLabel(property: MapMarkerProperty) {
  const parts: string[] = [];
  if (property.listPriceCents != null) {
    parts.push(`Property listed at $${(property.listPriceCents / 100).toLocaleString()}`);
  } else {
    parts.push('Property');
  }
  const beds = property.bedrooms != null ? `${property.bedrooms} beds` : null;
  const baths = property.bathrooms != null ? `${property.bathrooms} baths` : null;
  const summary = [beds, baths].filter(Boolean).join(', ');
  if (summary) {
    parts.push(summary);
  }
  if (property.isLiked) {
    parts.push('favorited');
  }
  return parts.join(', ');
}

type PopupImageProps = {
  uri: string | null;
  alt: string;
  className: string;
};

const PopupImage = ({ uri, alt, className }: PopupImageProps) => {
  return (
    <PropertyPhotoImage
      alt={alt}
      src={uri}
      containerClassName={className}
      className="h-full w-full object-cover"
    />
  );
};

type PopupBodyProps = {
  property: MapMarkerProperty;
};

const PopupBody = ({ property }: PopupBodyProps) => {
  const slots = buildPhotoSlots(property.photoUrls);

  return (
    <Col className={styles.popupEnter}>
      <PopupImage
        uri={slots.primary}
        alt="map-marker-primary-property-image"
        className="aspect-[7/3] w-88 rounded-t-2xl"
      />
      <Row className="justify-between pt-1.5">
        <PopupImage
          uri={slots.secondary}
          alt="map-marker-secondary-property-image"
          className="aspect-[43/25] w-43"
        />
        <PopupImage
          uri={slots.tertiary}
          alt="map-marker-tertiary-property-image"
          className="aspect-[43/25] w-43"
        />
      </Row>
      <Col className="gap-1.5 px-5 pb-4 pt-5">
        <Row className="items-baseline justify-between">
          {property.listPriceCents != null ? (
            <Text as="p" className="text-label-xl text-od-content-primary">
              {`$${(property.listPriceCents / 100).toLocaleString()}`}
            </Text>
          ) : null}
          {property.isLiked ? (
            <IconHeartFilled size={14} className="self-center text-od-content-error" />
          ) : null}
        </Row>
        {(() => {
          const stats = [
            property.bedrooms != null ? `${property.bedrooms} bds` : null,
            property.bathrooms != null ? `${property.bathrooms} ba` : null,
            property.totalLivingSquareFootage != null
              ? `${property.totalLivingSquareFootage.toLocaleString()} sqft`
              : null,
          ].filter((part): part is string => part !== null);
          if (stats.length === 0) {
            return null;
          }
          return (
            <Row className="items-baseline">
              {stats.map((part, index) => (
                <Fragment key={part}>
                  {index > 0 ? (
                    <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                      {' • '}
                    </Text>
                  ) : null}
                  <Text as="span" className="text-body-sm text-od-content-secondary">
                    {part}
                  </Text>
                </Fragment>
              ))}
            </Row>
          );
        })()}
        {property.street ? (
          <Text as="p" className="truncate text-body-sm text-od-content-tertiary">
            {property.street}
          </Text>
        ) : null}
      </Col>
    </Col>
  );
};

type MapMarkerPopupProps = {
  popupId: string;
  property: MapMarkerProperty;
};

export const MapMarkerPopup = ({ popupId, property }: MapMarkerPopupProps) => {
  return (
    <Popup
      longitude={property.longitude}
      latitude={property.latitude}
      anchor="bottom"
      offset={20}
      closeButton={false}
    >
      <Box id={popupId} role="region">
        <PopupBody property={property} />
      </Box>
    </Popup>
  );
};
